/**
 * ServerLoginService communicates with a Mutara Server instance and 
 * exchanges the necessary information to establish and maintain an 
 * HttpClient connection using the XSRF standards.
 */

import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpResponse, HttpEvent, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, ObservableInput } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ContextService, ApplicationConfig, ServerServiceConfig, LayoutInterface, LoginData } from './context.service';
import { NavigationStart, Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';

@Injectable({
  providedIn: 'root'
})
export class ServerLoginService implements HttpInterceptor {
  private token: string | null = null;
  private adminToken: string | null = null;
  private logoutTime: number = 0;
  static logoutTimer: number = -1;
  static configService: ServerServiceConfig;

  constructor(
    public http: HttpClient,
    private router: Router,
    private context: ContextService,
    private deviceService: DeviceDetectorService) {
    // Reload stored token
    // context.subscribeTo("layoutIdentity", (layout: LayoutInterface) => {
    //   if (!this.token || this.token.startsWith('{')) {
    //     this.setToken(this.getSecureToken(), layout);
    //   }
    // });
    context.subscribeTo("loginData", (loginData: LoginData) => {
      try {
        sessionStorage.setItem('login', JSON.stringify(loginData ?? {}))
      } catch (err) {
        console.error("Can't set local storage %O %O", loginData, err);
      }
    });
    context.subscribeTo("logout", (clearSession: boolean) => {
      ServerLoginService.logout(http, {
        error: () => {
          context.__clearSession();
          this.setToken(null);
          this.router.navigate(['/apply']);
        },
        next: () => {
          context.__clearSession();
          this.setToken(null);
          this.router.navigate(['/apply'], { replaceUrl: true });
        }
      });
    });
    context.subscribeTo("stepIndex", (stepName: string) => {
        sessionStorage.setItem('step', stepName);
    });
    context.subscribeTo("layoutIdentity", (layout : LayoutInterface) => {
      try {
      sessionStorage.setItem('layout', JSON.stringify(layout));
      } catch ( err ) {
        console.error('Error saving layout to session %O', err );
      }
    });

    router.events.subscribe({
      next: (value) => {
        if (value instanceof NavigationStart) {
          const data: any = this.router.getCurrentNavigation()?.extras?.state;
          console.log('Current Token %O', this.getSecureToken());
          console.log('Refresh Called %O %O ', this.token, value);
          try {
            const token = data?.token ?? sessionStorage.getItem('token'),
              loginData = data?.login ?? JSON.parse(sessionStorage.getItem('login') ?? '{}')
            console.log('$%$ Token %O $%$ LOGIN %O $%$', token, loginData);
            if (token && token != '' && loginData.username) {
              this.setToken(token);
              this.context.nextMessage('loginData', loginData);
            } else {
              this.context.nextMessage('logout', true);
            }
          } catch (err) {
            console.error('Router error %O', err);
            this.context.nextMessage('logout', true);
          }
        }
      }
    });
  }
  /**
   * Static method to force the logout of a user
   * @param http 
   * @param callback 
   */
  static logout(http: HttpClient, callback: any) {
    if (!!ServerLoginService?.configService?.serviceUrl) {
      http.post('$service/logout', {}).subscribe(callback);
    }
  }

  /**
   * This is where we inject the configuration at start time
   */
  load(): (() => Promise<boolean>) {
    return (): Promise<boolean> => {
      return new Promise<boolean>((resolve: (a: boolean) => void): void => {
        /*      // Here we can pull the config url from the .json file so we can connect to chat
              import('ngx-socket-io').then((module) => {
                console.log("Socket.io available, bootstrap configuration...");
        */
        this.context.addNewMessage('_token', null);
        console.log('%%%%%%%%%%%%%%\n\nLogin Service load called by injector\n\n%%%%%%%%%%%%%%');
        this.http.get('https://api.ipify.org?format=json').subscribe({
          error: (err) => console.error('Client IP Error %O', err),
          next: (result: any) => this.context.setClientIp(result.ip)
        });

        // subscribe to the loopback config request 
        this.http.get<ApplicationConfig>('/assets/config.json').subscribe(
          {
            error: (httpError: { status: number }): ObservableInput<{}> => {
              this.context.changeMessage('System Setup Error: Unable to access config.json');
              if (httpError.status !== 404) {
                resolve(false);
              }
              console.log('Default Config with error %O', httpError);

              // module.SocketIoModule.forRoot({ url: "http://localhost:8080", options: {} });
              this.router.navigate(['/error']);
              resolve(httpError.status < 300);
              return of({});
            },
            next: (local: ApplicationConfig) => {

              // Received the response we want, set the data locally for use, ensure chat is set to use the data
              if (!local.serviceUrl) {
                local.serviceUrl = 'http://localhost:8080'; // DEFAULT
              }
              // Now we subscribe to the service version request 
              this.http.get<ServerServiceConfig>(local.serviceUrl + '/version', { params: { theme: local.themeDir } }).subscribe({
                error: (httpError: { status: number }): ObservableInput<{}> => {
                  this.context.changeMessage('Enable to connect to serivce');
                  console.log('Error getting version information %O', httpError);
                  resolve(false);
                  return this.router.navigateByUrl('/error');
                },
                next: (serviceConfig: ServerServiceConfig) => {
                  ServerLoginService.configService = Object.assign(local, serviceConfig);
                  console.log('Setting application data %O', ServerLoginService.configService);
                  /*
                    console.log('reset the chat server ' + local.serviceUrl);
    
                    module.SocketIoModule.forRoot({
                      url: ServerLoginService.configService.serviceUrl,
                      options: {}
                    });
                    */
                  this.context.setApplicationInformation(ServerLoginService.configService);
                  resolve(true);
                }
              });
            }
          });

      });
    };
  }

  /**
   * Only set the value if it is in the correct structure
   * @param token
   * @param currentLayout (LayoutInterface) 
   */
  setToken(token: string | null, currentLayout: LayoutInterface | undefined = undefined): void {
    console.log('Setting token %O', token);

    if (!!token) {
      this.token = token;
    } else if ((token === undefined || token === null) && !!currentLayout) {
      this.token = JSON.stringify({ PUBLIC: currentLayout });
    }
    this.context.nextMessage('_token', this.token);
    sessionStorage.setItem('token', this.token ?? '');
  }
  getSecureToken(): string | null {
    return this.token;
  }

  private needToken(request: HttpRequest<any>): boolean {
    return request.url.includes('$service') && !((!!request.body && !!request.body.user) || request.url.endsWith('/version'));
  }

  /**
   * Add the security token to the request
   */
  addSecureToken(request: HttpRequest<any>): HttpRequest<any> {
    let addToken = this.needToken(request), token: string | null = this.getSecureToken();
    if (addToken) {
      request = request.clone({ withCredentials: true, setHeaders: { 'Authorization': token ?? '' } });
    } else if (request.url.startsWith('$service')) {
      request = request.clone({ withCredentials: true, setHeaders: { 'X-Mutara-Client': this.context.clientIp } });
    } else {
      console.error('Adding no token');
    }
    return request;
  }
  /** 
   * This intercepts the request to the server and attempts to add the security token  
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // This adds the current authorization token to the outgoing header
    // Special Case, when delegating, we need to keep the original admin token so we can return after the delegation is completed
    switch (request.url) {
      case '$service/delegate':
        // Store the admin token 
        this.adminToken = this.token;
        break;
    }
    if (request.method != 'OPTIONS') {
      this.context.changeMessage('');
    }
    // Change the url to point to the logged in service url as the local config should have been set!
    if (request.url.includes('$service')) {
      request = this.addSecureToken(request);
      if (!!ServerLoginService?.configService?.serviceUrl) {
        request = request.clone({ url: request.url.replace('$service', ServerLoginService.configService.serviceUrl) });
      } else {
        return next.handle(request.clone({ url: '/' }));
      }
    }

    // send cloned request with header to the next handler and catch the response
    return next.handle(request).pipe(
      map(
        (resp: any) => {
          return this.handleResponse(resp);
        }),
      catchError((error: HttpErrorResponse) => {
        if (error.status > 399) {
          if (error.status != 401) {
            this.context.changeMessage(`Error: ${error.error}`);
          }
          this.context.setFailedLogin(true);
          console.log("post login fail %O", error);
        }
        return throwError(() => error);
      })
    );
  }

  /** Handle the correct response */
  handleResponse(response: HttpResponse<any>): HttpEvent<any> {
    if (response instanceof HttpResponse) {
      if (response.url?.endsWith('logout')) {
        sessionStorage.clear();
        console.log('Clearing authentication %O', response.url);
        this.setToken(null);
        this.context.unsetLoginData();
      }
      else if (response.url?.endsWith('clear-delegate')) {
        console.log('reset delegate to admin');
        this.token = this.adminToken;
      }
      else if (!!response.body) {
        let body = response.body;
        if (!!body.token) {
          this.context.setFailedLogin(false);
          console.log('\n\n\nNew login %O', body.username);
          this.setToken(body.token);
          delete body.token;
          //   console.log( 'Should be user info %O', body ); 
          if (body.type !== undefined) {
            this.logoutTime = body.logoutTime;
            this.context.setLoginData(
              {
                username: body.username || body.email,
                nickname: body.nickname,
                phone: body.cellphone,
                firstname: body.firstname,
                lastname: body.lastname,
                email: body.email || body.username,
                lastCluster: body.lastCluster,
                defaultLayout: body.defaultLayout,
                //  editor: true,
              });
          }
          response = response.clone({ body: body });
          this.context.changeMessage('');
        }
        // Static 
        if (!!this.logoutTime) {
          if (ServerLoginService.logoutTimer != -1) {
            clearTimeout(ServerLoginService.logoutTimer);
            ServerLoginService.logoutTimer = -1;
          }

          ServerLoginService.logoutTimer = setTimeout(() => {
            ServerLoginService.logout(this.http, {});
          }, this.logoutTime);
        }
      }
    }
    return response;
  }

}
